import type { CSSProperties } from "react";
import { siteBuilderPartOptionKey } from "../siteBuilderStyles";
import type { ShopTemplateBlock } from "../types";

export type MorrowHoverEffect =
    | "blur"
    | "brighten"
    | "darken"
    | "glow"
    | "lift"
    | "lift-soft"
    | "outline"
    | "rotate"
    | "scale"
    | "scale-sm"
    | "shadow"
    | "skew"
    | "slide-right"
    | "underline";

export type MorrowAppearanceAnimation =
    | "blur-in"
    | "bounce-in"
    | "clip-down"
    | "clip-left"
    | "clip-right"
    | "clip-up"
    | "fade-down"
    | "fade-in"
    | "fade-left"
    | "fade-right"
    | "fade-up"
    | "flip-x"
    | "flip-y"
    | "float-in"
    | "reveal-down"
    | "reveal-left"
    | "reveal-right"
    | "reveal-up"
    | "rotate-in"
    | "zoom-in"
    | "zoom-out";

export type MorrowAnimationEffect = "fade" | "zoom" | "flip" | "rotate" | "blur" | "bounce" | "float" | "clip" | "reveal";
export type MorrowAnimationDirection = "none" | "up" | "down" | "left" | "right" | "x" | "y" | "in" | "out";
export type MorrowAnimationMode = "appearance" | "scroll";
export type MorrowAnimationTrigger = "once" | "always";

type MorrowInteractionOption =
    | "animation"
    | "animationDelay"
    | "animationDirection"
    | "animationDuration"
    | "animationEffect"
    | "animationMode"
    | "animationScrollEnd"
    | "animationScrollStart"
    | "animationStartAfter"
    | "animationTrigger"
    | "hoverBackgroundColor"
    | "hoverBorderColor"
    | "hoverDuration"
    | "hoverEffect"
    | "hoverTextColor";

export type MorrowInteractionStyle = CSSProperties & Partial<Record<`--morrow-${string}`, string>>;

export type MorrowInteractionAttributes = {
    "data-shop-animation"?: MorrowAppearanceAnimation;
    "data-shop-animation-mode"?: MorrowAnimationMode;
    "data-shop-animation-trigger"?: MorrowAnimationTrigger;
    "data-shop-hover-colors"?: "true";
    "data-shop-hover-effect"?: MorrowHoverEffect;
};

export type MorrowInteractionProps = MorrowInteractionAttributes & {
    style?: MorrowInteractionStyle;
};

const hoverEffects: ReadonlySet<MorrowHoverEffect> = new Set([
    "blur",
    "brighten",
    "darken",
    "glow",
    "lift",
    "lift-soft",
    "outline",
    "rotate",
    "scale",
    "scale-sm",
    "shadow",
    "skew",
    "slide-right",
    "underline",
]);

const appearanceAnimations: ReadonlySet<MorrowAppearanceAnimation> = new Set([
    "blur-in",
    "bounce-in",
    "clip-down",
    "clip-left",
    "clip-right",
    "clip-up",
    "fade-down",
    "fade-in",
    "fade-left",
    "fade-right",
    "fade-up",
    "flip-x",
    "flip-y",
    "float-in",
    "reveal-down",
    "reveal-left",
    "reveal-right",
    "reveal-up",
    "rotate-in",
    "zoom-in",
    "zoom-out",
]);

const animationEffects: ReadonlySet<MorrowAnimationEffect> = new Set([
    "fade",
    "zoom",
    "flip",
    "rotate",
    "blur",
    "bounce",
    "float",
    "clip",
    "reveal",
]);

const animationDirections: ReadonlySet<MorrowAnimationDirection> = new Set([
    "none",
    "up",
    "down",
    "left",
    "right",
    "x",
    "y",
    "in",
    "out",
]);

const animationKeyframes: Record<MorrowAppearanceAnimation, string> = {
    "blur-in": "morrowBlurIn",
    "bounce-in": "morrowBounceIn",
    "clip-down": "morrowClipDown",
    "clip-left": "morrowClipLeft",
    "clip-right": "morrowClipRight",
    "clip-up": "morrowClipUp",
    "fade-down": "morrowFadeDown",
    "fade-in": "morrowFadeIn",
    "fade-left": "morrowFadeLeft",
    "fade-right": "morrowFadeRight",
    "fade-up": "morrowFadeUp",
    "flip-x": "morrowFlipX",
    "flip-y": "morrowFlipY",
    "float-in": "morrowFloatIn",
    "reveal-down": "morrowRevealDown",
    "reveal-left": "morrowRevealLeft",
    "reveal-right": "morrowRevealRight",
    "reveal-up": "morrowRevealUp",
    "rotate-in": "morrowRotateIn",
    "zoom-in": "morrowZoomIn",
    "zoom-out": "morrowZoomOut",
};

const animationInitialClipPaths: Partial<Record<MorrowAppearanceAnimation, string>> = {
    "clip-down": "inset(0 0 100% 0)",
    "clip-left": "inset(0 0 0 100%)",
    "clip-right": "inset(0 100% 0 0)",
    "clip-up": "inset(100% 0 0 0)",
    "reveal-down": "inset(0 0 100% 0)",
    "reveal-left": "inset(0 100% 0 0)",
    "reveal-right": "inset(0 0 0 100%)",
    "reveal-up": "inset(100% 0 0 0)",
};

function optionKey(option: MorrowInteractionOption, part?: string): string {
    if (!part) {
        return option;
    }

    return siteBuilderPartOptionKey(part, option);
}

function optionText(block: ShopTemplateBlock | null | undefined, option: MorrowInteractionOption, part?: string): string {
    const options = block?.options;

    if (!options || typeof options !== "object" || Array.isArray(options)) {
        return "";
    }

    const value = options[optionKey(option, part)];

    if (typeof value === "string") {
        return value.trim();
    }

    return typeof value === "number" && Number.isFinite(value) ? String(value) : "";
}

function normalizedHoverEffect(value: string): MorrowHoverEffect | "" {
    return hoverEffects.has(value as MorrowHoverEffect) ? value as MorrowHoverEffect : "";
}

function normalizedAnimation(value: string): MorrowAppearanceAnimation | "" {
    return appearanceAnimations.has(value as MorrowAppearanceAnimation) ? value as MorrowAppearanceAnimation : "";
}

function normalizedAnimationEffect(value: string): MorrowAnimationEffect | "" {
    return animationEffects.has(value as MorrowAnimationEffect) ? value as MorrowAnimationEffect : "";
}

function normalizedAnimationDirection(value: string): MorrowAnimationDirection {
    return animationDirections.has(value as MorrowAnimationDirection) ? value as MorrowAnimationDirection : "none";
}

function normalizedAnimationMode(value: string): MorrowAnimationMode | "" {
    return value === "appearance" || value === "scroll" ? value : "";
}

function normalizedAnimationTrigger(value: string): MorrowAnimationTrigger {
    return value === "always" ? "always" : "once";
}

function animationFromEffect(effect: MorrowAnimationEffect, direction: MorrowAnimationDirection): MorrowAppearanceAnimation {
    if (effect === "fade") {
        if (direction === "up") return "fade-up";
        if (direction === "down") return "fade-down";
        if (direction === "left") return "fade-left";
        if (direction === "right") return "fade-right";

        return "fade-in";
    }

    if (effect === "zoom") {
        return direction === "out" ? "zoom-out" : "zoom-in";
    }

    if (effect === "flip") {
        return direction === "y" ? "flip-y" : "flip-x";
    }

    if (effect === "clip") {
        if (direction === "down") return "clip-down";
        if (direction === "left") return "clip-left";
        if (direction === "right") return "clip-right";

        return "clip-up";
    }

    if (effect === "reveal") {
        if (direction === "up") return "reveal-up";
        if (direction === "down") return "reveal-down";
        if (direction === "right") return "reveal-right";

        return "reveal-left";
    }

    if (effect === "blur") return "blur-in";
    if (effect === "bounce") return "bounce-in";
    if (effect === "float") return "float-in";

    return "rotate-in";
}

function cssTimeValue(value: string): string {
    if (/^\d+(\.\d+)?$/.test(value)) {
        return `${value}ms`;
    }

    return /^\d+(\.\d+)?m?s$/.test(value) ? value : "";
}

function cssPercentValue(value: string): string {
    if (value === "") {
        return "";
    }

    const numeric = Number(value.endsWith("%") ? value.slice(0, -1) : value);

    return Number.isFinite(numeric) ? `${Math.max(0, Math.min(100, numeric))}%` : "";
}

function resolvedAnimation(block: ShopTemplateBlock | null | undefined, part?: string): {
    animation: MorrowAppearanceAnimation | "";
    mode: MorrowAnimationMode;
    trigger: MorrowAnimationTrigger;
} {
    const legacyAnimation = normalizedAnimation(optionText(block, "animation", part));
    const explicitMode = normalizedAnimationMode(optionText(block, "animationMode", part));
    const effect = normalizedAnimationEffect(optionText(block, "animationEffect", part));
    const direction = normalizedAnimationDirection(optionText(block, "animationDirection", part));

    return {
        animation: effect
            ? animationFromEffect(effect, direction)
            : legacyAnimation || (explicitMode ? animationFromEffect("fade", direction) : ""),
        mode: explicitMode || "appearance",
        trigger: normalizedAnimationTrigger(optionText(block, "animationTrigger", part)),
    };
}

function applyHoverEffectStyle(effect: MorrowHoverEffect, style: MorrowInteractionStyle): void {
    if (effect === "lift") {
        style["--morrow-hover-transform"] = "translateY(-8px)";
        style["--morrow-hover-shadow"] = "0 22px 55px rgba(0,0,0,0.16)";
    } else if (effect === "lift-soft") {
        style["--morrow-hover-transform"] = "translateY(-4px)";
        style["--morrow-hover-shadow"] = "0 14px 34px rgba(0,0,0,0.12)";
    } else if (effect === "scale") {
        style["--morrow-hover-transform"] = "scale(1.045)";
    } else if (effect === "scale-sm") {
        style["--morrow-hover-transform"] = "scale(1.02)";
    } else if (effect === "shadow") {
        style["--morrow-hover-shadow"] = "0 18px 44px rgba(0,0,0,0.14)";
    } else if (effect === "glow") {
        style["--morrow-hover-shadow"] = "0 0 0 1px color-mix(in srgb, var(--site-primary) 28%, transparent), 0 0 34px color-mix(in srgb, var(--site-primary) 26%, transparent)";
    } else if (effect === "outline") {
        style["--morrow-hover-border-color"] = "var(--site-primary)";
        style["--morrow-hover-shadow"] = "0 0 0 2px color-mix(in srgb, var(--site-primary) 28%, transparent)";
    } else if (effect === "underline") {
        style["--morrow-hover-underline"] = "1";
    } else if (effect === "slide-right") {
        style["--morrow-hover-transform"] = "translateX(8px)";
    } else if (effect === "rotate") {
        style["--morrow-hover-transform"] = "rotate(1.6deg)";
    } else if (effect === "skew") {
        style["--morrow-hover-transform"] = "skewX(-2deg)";
    } else if (effect === "brighten") {
        style["--morrow-hover-filter"] = "brightness(1.08)";
    } else if (effect === "darken") {
        style["--morrow-hover-filter"] = "brightness(0.88)";
    } else if (effect === "blur") {
        style["--morrow-hover-filter"] = "blur(1.25px)";
    }
}

/**
 * `part` is an Inspector option scope (for example `title` or `primaryButton`),
 * not the editor's nested slot path stored in `data-shop-block-part`.
 */
export function morrowInteractionStyle(
    block: ShopTemplateBlock | null | undefined,
    part?: string,
): MorrowInteractionStyle | undefined {
    const hoverEffect = normalizedHoverEffect(optionText(block, "hoverEffect", part));
    const hoverDuration = cssTimeValue(optionText(block, "hoverDuration", part));
    const hoverTextColor = optionText(block, "hoverTextColor", part);
    const hoverBackgroundColor = optionText(block, "hoverBackgroundColor", part);
    const hoverBorderColor = optionText(block, "hoverBorderColor", part);
    const { animation, mode, trigger } = resolvedAnimation(block, part);
    const animationDuration = cssTimeValue(optionText(block, "animationDuration", part));
    const animationDelay = cssTimeValue(
        optionText(block, "animationStartAfter", part) || optionText(block, "animationDelay", part),
    );
    const animationScrollStart = cssPercentValue(optionText(block, "animationScrollStart", part));
    const animationScrollEnd = cssPercentValue(optionText(block, "animationScrollEnd", part));
    const style: MorrowInteractionStyle = {};

    if (hoverDuration) style["--morrow-hover-duration"] = hoverDuration;
    if (hoverTextColor) style["--morrow-hover-text-color"] = hoverTextColor;
    if (hoverBackgroundColor) style["--morrow-hover-background-color"] = hoverBackgroundColor;
    if (hoverBorderColor) style["--morrow-hover-border-color"] = hoverBorderColor;
    if (hoverEffect) applyHoverEffectStyle(hoverEffect, style);

    if (animation) {
        const animationName = animationKeyframes[animation];

        style["--morrow-animation-name"] = animationName;
        style["--morrow-animation-mode"] = mode;
        style["--morrow-animation-trigger"] = trigger;
        style["--morrow-animation-duration"] = animationDuration || "700ms";
        style["--morrow-animation-delay"] = animationDelay || "0ms";
        style.animationName = animationName;
        style.animationDuration = animationDuration || "700ms";
        style.animationDelay = animationDelay || "0ms";
        style.animationFillMode = "both";
        style.animationTimingFunction = "cubic-bezier(0.16, 1, 0.3, 1)";

        if (animationScrollStart) style["--morrow-animation-scroll-start"] = animationScrollStart;
        if (animationScrollEnd) style["--morrow-animation-scroll-end"] = animationScrollEnd;

        const initialClipPath = animationInitialClipPaths[animation];

        if (initialClipPath) style["--morrow-animation-initial-clip-path"] = initialClipPath;
    }

    return Object.keys(style).length > 0 ? style : undefined;
}

/** Uses the same Inspector option scope rules as {@link morrowInteractionStyle}. */
export function morrowInteractionAttributes(
    block: ShopTemplateBlock | null | undefined,
    part?: string,
): MorrowInteractionAttributes {
    const hoverEffect = normalizedHoverEffect(optionText(block, "hoverEffect", part));
    const hasHoverColors = Boolean(
        optionText(block, "hoverTextColor", part)
        || optionText(block, "hoverBackgroundColor", part)
        || optionText(block, "hoverBorderColor", part),
    );
    const { animation, mode, trigger } = resolvedAnimation(block, part);

    return {
        ...(hoverEffect ? { "data-shop-hover-effect": hoverEffect } : {}),
        ...(hasHoverColors ? { "data-shop-hover-colors": "true" as const } : {}),
        ...(animation ? {
            "data-shop-animation": animation,
            "data-shop-animation-mode": mode,
            "data-shop-animation-trigger": trigger,
        } : {}),
    };
}

export function morrowInteractionProps(
    block: ShopTemplateBlock | null | undefined,
    part?: string,
): MorrowInteractionProps {
    const style = morrowInteractionStyle(block, part);

    return {
        ...morrowInteractionAttributes(block, part),
        ...(style ? { style } : {}),
    };
}

export const MORROW_INTERACTION_CSS = `
:where([data-shop-hover-effect], [data-shop-hover-colors], [style*="--morrow-hover-"]) {
    transition-duration: var(--morrow-hover-duration, 180ms);
    transition-property: transform, box-shadow, filter, color, background-color, border-color, text-decoration-color;
    transition-timing-function: cubic-bezier(0.2, 0.8, 0.2, 1);
}

[data-shop-hover-effect="lift"]:is(:hover, :focus-visible, :focus-within) {
    box-shadow: var(--morrow-hover-shadow, 0 22px 55px rgba(0,0,0,0.16)) !important;
    transform: var(--morrow-hover-transform, translateY(-8px)) !important;
}

[data-shop-hover-effect="lift-soft"]:is(:hover, :focus-visible, :focus-within) {
    box-shadow: var(--morrow-hover-shadow, 0 14px 34px rgba(0,0,0,0.12)) !important;
    transform: var(--morrow-hover-transform, translateY(-4px)) !important;
}

[data-shop-hover-effect="scale"]:is(:hover, :focus-visible, :focus-within) {
    transform: var(--morrow-hover-transform, scale(1.045)) !important;
}

[data-shop-hover-effect="scale-sm"]:is(:hover, :focus-visible, :focus-within) {
    transform: var(--morrow-hover-transform, scale(1.02)) !important;
}

[data-shop-hover-effect="shadow"]:is(:hover, :focus-visible, :focus-within) {
    box-shadow: var(--morrow-hover-shadow, 0 18px 44px rgba(0,0,0,0.14)) !important;
}

[data-shop-hover-effect="glow"]:is(:hover, :focus-visible, :focus-within) {
    box-shadow: var(--morrow-hover-shadow, 0 0 0 1px color-mix(in srgb, var(--site-primary) 28%, transparent), 0 0 34px color-mix(in srgb, var(--site-primary) 26%, transparent)) !important;
}

[data-shop-hover-effect="outline"]:is(:hover, :focus-visible, :focus-within) {
    border-color: var(--morrow-hover-border-color, var(--site-primary)) !important;
    box-shadow: var(--morrow-hover-shadow, 0 0 0 2px color-mix(in srgb, var(--site-primary) 28%, transparent)) !important;
}

[data-shop-hover-effect="rotate"]:is(:hover, :focus-visible, :focus-within) {
    transform: var(--morrow-hover-transform, rotate(1.6deg)) !important;
}

[data-shop-hover-effect="skew"]:is(:hover, :focus-visible, :focus-within) {
    transform: var(--morrow-hover-transform, skewX(-2deg)) !important;
}

[data-shop-hover-effect="slide-right"]:is(:hover, :focus-visible, :focus-within) {
    transform: var(--morrow-hover-transform, translateX(8px)) !important;
}

[data-shop-hover-effect="brighten"]:is(:hover, :focus-visible, :focus-within) {
    filter: var(--morrow-hover-filter, brightness(1.08)) !important;
}

[data-shop-hover-effect="darken"]:is(:hover, :focus-visible, :focus-within) {
    filter: var(--morrow-hover-filter, brightness(0.88)) !important;
}

[data-shop-hover-effect="blur"]:is(:hover, :focus-visible, :focus-within) {
    filter: var(--morrow-hover-filter, blur(1.25px)) !important;
}

[data-shop-hover-effect="underline"]:is(:hover, :focus-visible, :focus-within) {
    text-decoration-line: underline !important;
    text-decoration-thickness: 2px;
    text-underline-offset: 0.22em;
}

[style*="--morrow-hover-transform"]:is(:hover, :focus-visible, :focus-within) {
    transform: var(--morrow-hover-transform) !important;
}

[style*="--morrow-hover-shadow"]:is(:hover, :focus-visible, :focus-within) {
    box-shadow: var(--morrow-hover-shadow) !important;
}

[style*="--morrow-hover-filter"]:is(:hover, :focus-visible, :focus-within) {
    filter: var(--morrow-hover-filter) !important;
}

[style*="--morrow-hover-background-color"]:is(:hover, :focus-visible, :focus-within) {
    background-color: var(--morrow-hover-background-color) !important;
}

[style*="--morrow-hover-border-color"]:is(:hover, :focus-visible, :focus-within) {
    border-color: var(--morrow-hover-border-color) !important;
}

[style*="--morrow-hover-text-color"]:is(:hover, :focus-visible, :focus-within) {
    color: var(--morrow-hover-text-color) !important;
}

:where([data-shop-animation]) {
    animation-delay: var(--morrow-animation-delay, 0ms);
    animation-duration: var(--morrow-animation-duration, 700ms);
    animation-fill-mode: both;
    animation-name: var(--morrow-animation-name);
    animation-timing-function: cubic-bezier(0.16, 1, 0.3, 1);
    will-change: transform, opacity, filter, clip-path;
}

[data-shop-animation-mode="appearance"] {
    animation-name: none !important;
    opacity: 0;
}

[data-shop-animation-mode="appearance"]:where(
    [data-shop-animation^="clip-"],
    [data-shop-animation^="reveal-"]
) {
    clip-path: var(--morrow-animation-initial-clip-path, inset(100% 0 0 0));
}

[data-shop-animation-mode="appearance"].shop-animation-visible {
    animation-name: var(--morrow-animation-name) !important;
    opacity: 1;
}

.shop-animation-settled {
    animation-name: none !important;
    opacity: 1 !important;
}

:where([data-shop-animation^="clip-"], [data-shop-animation^="reveal-"]).shop-animation-visible {
    clip-path: inset(0 0 0 0);
}

:where([data-shop-animation^="clip-"], [data-shop-animation^="reveal-"]).shop-animation-settled {
    clip-path: none !important;
}

@supports (animation-timeline: view()) {
    [data-shop-animation-mode="scroll"] {
        animation-delay: 0ms;
        animation-range: entry var(--morrow-animation-scroll-start, 0%) cover var(--morrow-animation-scroll-end, 60%);
        animation-timeline: view();
    }
}

@supports not (animation-timeline: view()) {
    [data-shop-animation-mode="scroll"] {
        animation-name: none !important;
        opacity: 0;
    }

    [data-shop-animation-mode="scroll"]:where(
        [data-shop-animation^="clip-"],
        [data-shop-animation^="reveal-"]
    ) {
        clip-path: var(--morrow-animation-initial-clip-path, inset(100% 0 0 0));
    }

    [data-shop-animation-mode="scroll"].shop-animation-visible {
        animation-name: var(--morrow-animation-name) !important;
        opacity: 1;
    }
}

@keyframes morrowFadeIn {
    from { opacity: 0; }
    to { opacity: 1; }
}

@keyframes morrowFadeUp {
    from { opacity: 0; transform: translateY(28px); }
    to { opacity: 1; transform: translateY(0); }
}

@keyframes morrowFadeDown {
    from { opacity: 0; transform: translateY(-28px); }
    to { opacity: 1; transform: translateY(0); }
}

@keyframes morrowFadeLeft {
    from { opacity: 0; transform: translateX(28px); }
    to { opacity: 1; transform: translateX(0); }
}

@keyframes morrowFadeRight {
    from { opacity: 0; transform: translateX(-28px); }
    to { opacity: 1; transform: translateX(0); }
}

@keyframes morrowZoomIn {
    from { opacity: 0; transform: scale(0.94); }
    to { opacity: 1; transform: scale(1); }
}

@keyframes morrowZoomOut {
    from { opacity: 0; transform: scale(1.06); }
    to { opacity: 1; transform: scale(1); }
}

@keyframes morrowFlipX {
    from { opacity: 0; transform: perspective(900px) rotateX(72deg); }
    to { opacity: 1; transform: perspective(900px) rotateX(0); }
}

@keyframes morrowFlipY {
    from { opacity: 0; transform: perspective(900px) rotateY(72deg); }
    to { opacity: 1; transform: perspective(900px) rotateY(0); }
}

@keyframes morrowRotateIn {
    from { opacity: 0; transform: rotate(-7deg) scale(0.96); }
    to { opacity: 1; transform: rotate(0) scale(1); }
}

@keyframes morrowBlurIn {
    from { opacity: 0; filter: blur(12px); transform: translateY(8px); }
    to { opacity: 1; filter: blur(0); transform: translateY(0); }
}

@keyframes morrowBounceIn {
    0% { opacity: 0; transform: scale(0.88); }
    58% { opacity: 1; transform: scale(1.035); }
    78% { transform: scale(0.985); }
    100% { opacity: 1; transform: scale(1); }
}

@keyframes morrowFloatIn {
    from { opacity: 0; transform: translateY(24px); }
    70% { opacity: 1; transform: translateY(-4px); }
    to { opacity: 1; transform: translateY(0); }
}

@keyframes morrowClipUp {
    from { opacity: 0; clip-path: inset(100% 0 0 0); transform: translateY(16px); }
    to { opacity: 1; clip-path: inset(0 0 0 0); transform: translateY(0); }
}

@keyframes morrowClipDown {
    from { opacity: 0; clip-path: inset(0 0 100% 0); transform: translateY(-16px); }
    to { opacity: 1; clip-path: inset(0 0 0 0); transform: translateY(0); }
}

@keyframes morrowClipLeft {
    from { opacity: 0; clip-path: inset(0 0 0 100%); transform: translateX(16px); }
    to { opacity: 1; clip-path: inset(0 0 0 0); transform: translateX(0); }
}

@keyframes morrowClipRight {
    from { opacity: 0; clip-path: inset(0 100% 0 0); transform: translateX(-16px); }
    to { opacity: 1; clip-path: inset(0 0 0 0); transform: translateX(0); }
}

@keyframes morrowRevealLeft {
    from { opacity: 0; clip-path: inset(0 100% 0 0); }
    to { opacity: 1; clip-path: inset(0 0 0 0); }
}

@keyframes morrowRevealRight {
    from { opacity: 0; clip-path: inset(0 0 0 100%); }
    to { opacity: 1; clip-path: inset(0 0 0 0); }
}

@keyframes morrowRevealUp {
    from { opacity: 0; clip-path: inset(100% 0 0 0); }
    to { opacity: 1; clip-path: inset(0 0 0 0); }
}

@keyframes morrowRevealDown {
    from { opacity: 0; clip-path: inset(0 0 100% 0); }
    to { opacity: 1; clip-path: inset(0 0 0 0); }
}

.shop-animation-restarting[data-shop-animation] {
    animation-name: none !important;
}

.shop-animation-restarting:where([data-shop-animation^="clip-"], [data-shop-animation^="reveal-"]) {
    clip-path: var(--morrow-animation-initial-clip-path, inset(100% 0 0 0)) !important;
}

@media (prefers-reduced-motion: reduce) {
    :where([data-shop-hover-effect], [data-shop-hover-colors]) {
        transition-duration: 1ms !important;
    }

    [data-shop-hover-effect]:is(:hover, :focus-visible, :focus-within) {
        filter: none !important;
        transform: none !important;
    }

    [data-shop-animation] {
        animation: none !important;
        clip-path: none !important;
        opacity: 1 !important;
    }
}
`;
